import { createMiddleware } from "@tanstack/react-start";
import { getRequestHeaders } from "@tanstack/react-start/server";
import {
  BRAND_IDENTITIES,
  type BrandIdentity,
  getRoutePrefixForHostname,
  identityForHostname,
} from "~/config/domains.ts";

/**
 * Hostname context added by the hostname middleware
 *
 * @since 1.0.0
 */
export interface HostnameContext {
  hostname: {
    /** The full host header value */
    full: string;
    /** Whether this is the ClawInstall domain */
    isClawInstall: boolean;
    /** Whether this is the Orca subdomain */
    isOrca: boolean;
    /** Whether this hostname maps to the FlirtMachine route prefix */
    isFlirtMachine: boolean;
    /**
     * The brand identity for the requesting host (ClawInstall or Consinna);
     * undefined for hosts outside the ClawInstall property.
     */
    identity?: BrandIdentity | undefined;
  };
}

/**
 * Derive the hostname context for a raw `Host`/`X-Forwarded-Host` value. The
 * single source of truth for host→brand resolution — used by both the server
 * middleware and the root route's `beforeLoad`, so the two can never drift.
 *
 * The route prefix from the ONE domain registry drives every flag (no substring
 * detector), and the brand identity is resolved from the same registry. The
 * `VITE_FORCE_*` overrides keep the forced-brand dev switches working.
 *
 * @since 1.0.0
 */
export const hostnameContextFor = (host: string): HostnameContext["hostname"] => {
  const hostname = host.split(":")[0].toLowerCase();
  const prefix = getRoutePrefixForHostname(hostname);
  const isClawInstall = prefix === "/clawinstall" || import.meta.env.VITE_FORCE_CLAWINSTALL === "true";
  const isOrca = prefix === "/orca" || import.meta.env.VITE_FORCE_ORCA === "true";
  const isFlirtMachine = prefix === "/flirtmachine" || import.meta.env.VITE_FORCE_FLIRTMACHINE === "true";
  const identity = identityForHostname(hostname) ?? (isClawInstall ? BRAND_IDENTITIES.clawinstall : undefined);
  return { full: host, isClawInstall, isOrca, isFlirtMachine, identity };
};

/**
 * Middleware that detects the hostname from the request and adds it to context.
 *
 * This enables hostname-based routing for multi-tenant scenarios where
 * clawinstall.ai serves different content than the main site.
 *
 * @since 1.0.0
 */
export const hostnameMiddleware = createMiddleware().server(
  // oxlint-disable-next-line eslint-js/no-restricted-syntax -- TanStack middleware requires async
  async ({ next }) => {
    const headers = getRequestHeaders();
    // X-Forwarded-Host contains original hostname when behind nginx ingress
    // Fall back to Host header for direct access
    const host = headers?.get("x-forwarded-host") || headers?.get("host") || "";

    const hostname = hostnameContextFor(host);

    console.log("[hostnameMiddleware] Hostname check:", {
      xForwardedHost: headers?.get("x-forwarded-host"),
      host: headers?.get("host"),
      hostname,
    });

    return next({ context: { hostname } });
  },
);
